import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface CableMTDetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};
`;

const ProductLayout = styled.div`
  display: flex;
  gap: 3rem;
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
  }
`;

const ThumbnailImage = styled.div`
  width: 80px;
  height: 80px;
  background: #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }
`;

const ProductInfoSection = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding: 1rem 0;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  
  @media (max-width: 768px) {
    align-items: center;
    padding: 0.1rem 0;
  }
`;

const ProductTitle = styled(motion.h1)<{ $isRTL: boolean }>`
  font-size: 2.2rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const ProductSubtitle = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  color: #64748b;
  margin-bottom: 2rem;
`;

const TechnicalDatasheet = styled.div<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 0.5rem auto;
  }
  

  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const DatasheetIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    filter: brightness(0) invert(1);
    width: 24px;
    height: 24px;
  }
`;

const DatasheetText = styled.div`
  display: flex;
  flex-direction: column;
`;

const DatasheetTitle = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
`;

const DatasheetFormat = styled.span`
  font-size: 0.85rem;
  color: #64748b;
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 0.5rem auto;
    
    img {
      max-width: 100px;
    }
  }
  

  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
`;

const ContentSection = styled.div<{ $isRTL: boolean }>`
  margin-top: 4rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const SectionTitle = styled(motion.h2)<{ $isRTL: boolean }>`
  font-size: 2.2rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    border-radius: 2px;
  }
`;

const ProductsSection = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
    padding: 1.5rem;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
`;

const ItemReference = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 1rem;
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
  min-width: 200px;
`;

const DetailValue = styled.span`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 600;
`;

const ItemDatasheet = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
    align-self: center;
    margin: 0.5rem auto;
  }
  


  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const DescriptionSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const DescriptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const DescriptionCard = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const CardLabel = styled.div<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const CardSubTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const CardValue = styled.div<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const CardText = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #475569;
  line-height: 1.7;
  margin: 0;
`;

const CharacteristicsSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  margin-bottom: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const CharacteristicItem = styled.div<{ $isRTL: boolean }>`
  background: white;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CharacteristicHeader = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  cursor: pointer;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #f8fafc 100%);
  }
`;

const CharacteristicTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  color: #0f172a;
  font-weight: 700;
  margin: 0;
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CharacteristicContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

const CharacteristicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 0.25rem;
  padding: 2rem;
`;

const CharacteristicRow = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 3px solid #dc2626;
  transition: all 0.3s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateX(4px);
  }
`;

const CharacteristicLabel = styled.span<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #475569;
  font-weight: 500;
`;

const CharacteristicValue = styled.span<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #0f172a;
  font-weight: 700;
`;

const ResourcesSection = styled.div<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  }
`;

const ResourcesContent = styled.div<{ $isRTL: boolean }>`
  background: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #dc2626;
  position: relative;
`;

const ResourcesSubTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.4rem;
  color: #0f172a;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const ResourcesText = styled.p<{ $isRTL: boolean }>`
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.8;
  margin: 0;
`;

const DocumentationSection = styled.div<{ $isRTL: boolean }>`
  margin-bottom: 2rem;
`;

const DocumentationItem = styled.div<{ $isRTL: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
  }
`;

const DocumentationInfo = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: column;
`;

const DocumentationTitle = styled.span<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #0f172a;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const DocumentationSize = styled.span<{ $isRTL: boolean }>`
  font-size: 0.9rem;
  color: #64748b;
`;

const DownloadButton = styled.button<{ $isRTL: boolean }>`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
  }
`;

const CableMTDetail: React.FC<CableMTDetailProps> = ({ t, isRTL }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [openCharacteristics, setOpenCharacteristics] = useState<{ [key: string]: boolean }>({});

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transport et distribution de l'énergie", path: "/category/cables-transport" },
    { label: t.mediumVoltageCablesTitle, path: "/category/cables-transport" },
    { label: "Cable MT NFC 33-226 18/30 (36) kV", path: "/product/cable-mt-nfc" },
  ];

  const product = {
    id: 'cable-mt-nfc',
    title: 'Cable MT NFC 33-226 18/30 (36) kV',
    subtitle: 'Câbles HTA 18/30 (36) kV cuivre ou aluminium',
    image: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cable MT NFC 33-226 1830 (36) kV.jpg',
    datasheet: '/images/catalog/Câbles de transport et de distribution de l\'énergie/Cable MT NFC 33-226 1830 (36) kV/Cable MT NFC 33-226 1830 (36) kV.pdf',
  };

  const products = [
    {
      id: 'nf-c-33-226-1x150',
      name: 'NF C 33-226 18/30KV 1x150 AL PE',
      reference: 'Réf. Nexans 57270150400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '150 mm²',
      conducteurs: '1',
      resistance: '0,12 Ohm/km',
      voltageDrop: '0,49 V/A.km',
      filename: 'NF C 33-226 1830KV 1x150 AL PE.pdf'
    },
    {
      id: 'nf-c-33-226-1x240',
      name: 'NF C 33-226 18/30KV 1x240 AL PE',
      reference: 'Réf. Nexans 57270240400',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '240 mm²',
      conducteurs: '1',
      resistance: '0,11 Ohm/km',
      voltageDrop: '0,34 V/A.km',
      filename: 'NF C 33-226 1830KV 1x240 AL PE.pdf'
    },
    {
      id: 'nf-c33-226-1x400',
      name: 'NF C33-226 18/30KV 1x400 AL PE',
      reference: 'Réf. Nexans 57270400500',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '400 mm²',
      conducteurs: '1',
      resistance: '0,09 Ohm/km',
      voltageDrop: '0,26 V/A.km',
      filename: 'NF C33-226 1830KV 1x400 AL PE.pdf'
    },
    {
      id: 'nf-c33-226-1x630',
      name: 'NF C33-226 18/30KV 1x630 AL PE',
      reference: 'Réf. Nexans 57270630500',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '630 mm²',
      conducteurs: '1',
      resistance: '0,11 Ohm/km',
      voltageDrop: '0,22 V/A.km',
      filename: 'NF C33-226 1830KV 1x630 AL PE.pdf'
    },
    {
      id: 'nfc33-226-1x240-c2',
      name: 'NFC33-226 18/30KV 1x240 AL C2',
      reference: 'Réf. Nexans 57270240520',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '240 mm²',
      conducteurs: '1',
      resistance: '0,11 Ohm/km',
      voltageDrop: '0,34 V/A.km',
      filename: 'NFC33-226 1830KV 1x240 AL C2.pdf'
    },
    {
      id: 'nfc33-226-1x240-cu-c2',
      name: 'NFC33-226 18/30KV 1x240 CU C2',
      reference: 'Réf. Nexans 57270240200',
      conditionnement: 'Touret (m)',
      nature: 'Cuivre',
      section: '240 mm²',
      conducteurs: '1',
      resistance: '0.11 Ohm/km',
      voltageDrop: '0.25 V/A.km',
      filename: 'NFC33-226 18/30KV 1x240 CU C2.pdf'
    },
    {
      id: 'nfc33-226-1x50-al',
      name: 'NFC33-226 18/30KV 1x50 AL',
      reference: 'Réf. Nexans 57270050500',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '50 mm²',
      conducteurs: '1',
      resistance: '0.12 Ohm/km',
      voltageDrop: '1.29 V/A.km',
      filename: 'NFC33-226 18/30KV 1x50 AL.pdf'
    },
    {
      id: 'nfc33-226-1x70-cu',
      name: 'NFC33-226 18/30KV 1x70 CU',
      reference: 'Réf. Nexans 57270070500',
      conditionnement: 'Touret (m)',
      nature: 'Cuivre',
      section: '70 mm²',
      conducteurs: '1',
      resistance: '0.14 Ohm/km',
      voltageDrop: '0.82 V/A.km',
      filename: 'NFC33-226 18/30KV 1x70 CU.pdf'
    },
    {
      id: 'nfc33-226-1x95-al',
      name: 'NFC33-226 18/30KV 1x95 AL',
      reference: 'Réf. Nexans 57270095500',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '95 mm²',
      conducteurs: '1',
      resistance: '0.13 Ohm/km',
      voltageDrop: '0.71 V/A.km',
      filename: 'NFC33-226 18/30KV 1x95 AL.pdf'
    },
    {
      id: 'nfc33-226-3x150-al',
      name: 'NFC33-226 18/30KV 3x150 AL',
      reference: 'Réf. Nexans 57275150500',
      conditionnement: 'Touret (m)',
      nature: 'Aluminium',
      section: '150 mm²',
      conducteurs: '3',
      resistance: '0.12 Ohm/km',
      voltageDrop: '0.49 V/A.km',
      filename: 'NFC33-226 18/30KV 3x150 AL.pdf'
    }
  ];

  const handleDatasheetClick = () => {
    window.open(product.datasheet, '_blank');
  };

  const handleProductDatasheet = (filename: string) => {
    const filePath = `/images/catalog/Câbles de transport et de distribution de l'énergie/Cable MT NFC 33-226 1830 (36) kV/produits/${filename}`;
    window.open(filePath, '_blank');
  };

  const toggleCharacteristic = (key: string) => {
    setOpenCharacteristics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDownload = (filename: string) => {
    const filePath = `/images/catalog/Câbles de transport et de distribution de l'énergie/Cable MT NFC 33-226 1830 (36) kV/${filename}`;
    window.open(filePath, '_blank');
  };

  return (
    <ProductDetailPage $isRTL={isRTL} ref={ref}>
      <Container>
        <Breadcrumbs 
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.label}>
              <BreadcrumbLink 
                $isLast={index === breadcrumbs.length - 1} 
                onClick={() => index !== breadcrumbs.length - 1 && navigate(crumb.path)}
              >
                {crumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && (
                <span>{isRTL ? '<' : '>'}</span>
              )}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={product.image} alt={product.title} />
            </MainImage>
            <ThumbnailImage>
              <img src={product.image} alt={`${product.title} thumbnail`} />
            </ThumbnailImage>
          </ImageSection>

          <ProductInfoSection $isRTL={isRTL}>
            <ProductTitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {product.title}
            </ProductTitle>

            <ProductSubtitle 
              $isRTL={isRTL}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              {product.subtitle}
            </ProductSubtitle>

            <TechnicalDatasheet 
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
            >
              <DatasheetIcon>
                <img 
                  src="/images/catalog/pdf.svg" 
                  alt="PDF icon" 
                />
              </DatasheetIcon>
              <DatasheetText>
                <DatasheetTitle>Fiche technique</DatasheetTitle>
                <DatasheetFormat>PDF</DatasheetFormat>
              </DatasheetText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfoSection>
        </ProductLayout>

        <ContentSection $isRTL={isRTL}>
          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Produits
          </SectionTitle>
          <ProductsSection>
            <ProductsList>
              {products.map((item) => (
                <ProductItem key={item.id}>
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemReference>{item.reference}</ItemReference>
                    <ItemDetails>
                      <DetailRow>
                        <DetailLabel>Conditionnement:</DetailLabel>
                        <DetailValue>{item.conditionnement}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Nature de l'âme:</DetailLabel>
                        <DetailValue>{item.nature}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Section du conducteur:</DetailLabel>
                        <DetailValue>{item.section}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Nombre de conducteurs:</DetailLabel>
                        <DetailValue>{item.conducteurs}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Conductor AC resistance at 50 Hz:</DetailLabel>
                        <DetailValue>{item.resistance}</DetailValue>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Voltage Drop:</DetailLabel>
                        <DetailValue>{item.voltageDrop}</DetailValue>
                      </DetailRow>
                    </ItemDetails>
                  </ItemInfo>
                  <ItemDatasheet onClick={() => handleProductDatasheet(item.filename)}>
                    <DatasheetIcon>
                      <img 
                        src="/images/catalog/pdf.svg" 
                        alt="PDF icon" 
                      />
                    </DatasheetIcon>
                    <DatasheetText>
                      <DatasheetTitle>Fiche technique</DatasheetTitle>
                      <DatasheetFormat>PDF</DatasheetFormat>
                    </DatasheetText>
                  </ItemDatasheet>
                </ProductItem>
              ))}
            </ProductsList>
          </ProductsSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Description
          </SectionTitle>
          <DescriptionSection $isRTL={isRTL}>
            <DescriptionGrid>
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Normes</CardTitle>
                <CardLabel $isRTL={isRTL}>PRODUCT</CardLabel>
                <CardValue $isRTL={isRTL}>NF C33-226</CardValue>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Usage</CardTitle>
                <CardSubTitle $isRTL={isRTL}>Utilisation</CardSubTitle>
                <CardText $isRTL={isRTL}>
                  Le câble MT NFC 33-228 est destiné à la distribution publique moyenne tension HTA 18/30 kV. La technologie de sa fabrication permet de le poser sans protection mécanique supplémentaire.
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Description</CardTitle>
                <CardText $isRTL={isRTL}>
                  Il peut être constitué de 1 ou 3 conducteurs de phase. L'étanchéité longitudinale est assurée par une poudre gonflante.
                </CardText>
              </DescriptionCard>
              
              <DescriptionCard $isRTL={isRTL}>
                <CardTitle $isRTL={isRTL}>Repérage</CardTitle>
                <CardText $isRTL={isRTL}>
                  Nexans - marquage sur les phases: 1,2,3 - Section - Alu ou Cu - Tension - Année de fabrication
                </CardText>
              </DescriptionCard>
            </DescriptionGrid>
          </DescriptionSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            Caractéristiques
          </SectionTitle>
          <CharacteristicsSection $isRTL={isRTL}>
            <CharacteristicItem $isRTL={isRTL}>
              <CharacteristicHeader 
                $isRTL={isRTL}
                onClick={() => toggleCharacteristic('construction')}
              >
                <CharacteristicTitle $isRTL={isRTL}>
                  Caractéristiques de construction
                </CharacteristicTitle>
                <ChevronIcon $isOpen={openCharacteristics.construction}>
                  ▼
                </ChevronIcon>
              </CharacteristicHeader>
              <CharacteristicContent $isOpen={openCharacteristics.construction}>
                <CharacteristicGrid>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Couleur de la gaine</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Noir</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Ecran</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Ruban aluminium longitudinal collé à la gaine extérieure</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Flexibilité de l'âme</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Câblé classe 2</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Forme de l'âme</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Circulaire</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Gaine extérieure</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>PE</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Isolation</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>XLPE</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Matière constituant l'étanchéité longitudinale</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Poudre gonflante</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Matière du semi-conducteur extérieur</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Elastomère extrudé cannelé pelable</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Matière du semi-conducteur intérieur</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Polyéthylène réticulé extrudé</CharacteristicValue>
                  </CharacteristicRow>
                </CharacteristicGrid>
              </CharacteristicContent>
            </CharacteristicItem>

            <CharacteristicItem $isRTL={isRTL}>
              <CharacteristicHeader 
                $isRTL={isRTL}
                onClick={() => toggleCharacteristic('electrical')}
              >
                <CharacteristicTitle $isRTL={isRTL}>
                  Caractéristiques électriques
                </CharacteristicTitle>
                <ChevronIcon $isOpen={openCharacteristics.electrical}>
                  ▼
                </ChevronIcon>
              </CharacteristicHeader>
              <CharacteristicContent $isOpen={openCharacteristics.electrical}>
                <CharacteristicGrid>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Tension de service nominale Uo/U</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>18/30 (36) kV</CharacteristicValue>
                  </CharacteristicRow>
                </CharacteristicGrid>
              </CharacteristicContent>
            </CharacteristicItem>

            <CharacteristicItem $isRTL={isRTL}>
              <CharacteristicHeader 
                $isRTL={isRTL}
                onClick={() => toggleCharacteristic('mechanical')}
              >
                <CharacteristicTitle $isRTL={isRTL}>
                  Caractéristiques mécaniques
                </CharacteristicTitle>
                <ChevronIcon $isOpen={openCharacteristics.mechanical}>
                  ▼
                </ChevronIcon>
              </CharacteristicHeader>
              <CharacteristicContent $isOpen={openCharacteristics.mechanical}>
                <CharacteristicGrid>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Résistance mécanique aux chocs</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Bonne</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Flexibilité du câble</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Rigide</CharacteristicValue>
                  </CharacteristicRow>
                </CharacteristicGrid>
              </CharacteristicContent>
            </CharacteristicItem>

            <CharacteristicItem $isRTL={isRTL}>
              <CharacteristicHeader 
                $isRTL={isRTL}
                onClick={() => toggleCharacteristic('usage')}
              >
                <CharacteristicTitle $isRTL={isRTL}>
                  Caractéristiques d'utilisation
                </CharacteristicTitle>
                <ChevronIcon $isOpen={openCharacteristics.usage}>
                  ▼
                </ChevronIcon>
              </CharacteristicHeader>
              <CharacteristicContent $isOpen={openCharacteristics.usage}>
                <CharacteristicGrid>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Non propagateur de la flamme</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>C2, NF C 32-070</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Résistance aux Intempéries</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Bonne</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Résistance chimique</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>Accidentelle</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Étanchéité</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>AD7</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Facteur de courbure en cours de pose</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>15 (xD)</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Température maximale sur l'âme</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>90 °C</CharacteristicValue>
                  </CharacteristicRow>
                  <CharacteristicRow $isRTL={isRTL}>
                    <CharacteristicLabel $isRTL={isRTL}>Température maximale sur l'âme en court-circuit</CharacteristicLabel>
                    <CharacteristicValue $isRTL={isRTL}>250 °C</CharacteristicValue>
                  </CharacteristicRow>
                </CharacteristicGrid>
              </CharacteristicContent>
            </CharacteristicItem>
          </CharacteristicsSection>

          <SectionTitle 
            $isRTL={isRTL}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Ressources
          </SectionTitle>
          <ResourcesSection $isRTL={isRTL}>
            <ResourcesContent $isRTL={isRTL}>
              <DocumentationSection $isRTL={isRTL}>
                <ResourcesSubTitle $isRTL={isRTL}>Documentation</ResourcesSubTitle>
                <DocumentationItem $isRTL={isRTL}>
                  <DocumentationInfo $isRTL={isRTL}>
                    <DocumentationTitle $isRTL={isRTL}>Catalogue MT</DocumentationTitle>
                    <DocumentationSize $isRTL={isRTL}>PDF - 3.2 MB</DocumentationSize>
                  </DocumentationInfo>
                  <DownloadButton 
                    $isRTL={isRTL}
                    onClick={() => handleDownload('Catalogue MT.pdf')}
                  >
                    Télécharger
                  </DownloadButton>
                </DocumentationItem>
              </DocumentationSection>

              <ResourcesSubTitle $isRTL={isRTL}>Information de livraison</ResourcesSubTitle>
              <ResourcesText $isRTL={isRTL}>
                Nous consulter pour toute autre variante.
              </ResourcesText>
            </ResourcesContent>
          </ResourcesSection>
        </ContentSection>
      </Container>
    </ProductDetailPage>
  );
};

export default CableMTDetail;
